"use client"

import { useEffect, useMemo, useRef } from "react"
import { Carousel } from "./carousel"
import gsap from "@/lib/gsap-init"
import { useFestivalSharedData } from "@/app/store/useFestivalSharedData"
import { useTranslations, useLocale } from 'next-intl'
import { useLocalizedField } from '@/hooks/useLocalizedField'

export function EventsSection () {
  const t = useTranslations()
  const locale = useLocale()
  const { getLocalizedField } = useLocalizedField()
  const {
    festivals,   // ⬅️ récupère les festivals
    loading,
    error,
    fetchFestivals, // ⬅️ fetch festivals
  } = useFestivalSharedData()

  const sectionRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)

  // 👉 Fetch festivals
  useEffect(() => {
    fetchFestivals()
  }, [fetchFestivals])

    const carouselItems = useMemo(() => {
      if (!festivals || festivals.length === 0) return []
  
      return festivals.map((a: any) => ({
        id: a.slug || a.id,
        title: getLocalizedField(a, 'name'),
        name: getLocalizedField( a.destination, 'state'),
        description: getLocalizedField(a, 'short_description') || getLocalizedField(a, 'description'),
        image: a.images?.[1] || a.images?.[0],
      }))
    }, [festivals, getLocalizedField])

  // 🎥 GSAP animations
  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      gsap.from(carouselRef.current, {
        opacity: 0,
        scale: 0.95,
        y: 60,
        duration: 0.8,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top center",
          end: "center center",
          scrub: 0.5,
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      id="festivals"
      ref={sectionRef}
      className="py-4 px-4 bg-background transition-colors duration-300"
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="max-w-7xl mx-auto">
        
        {/* LOADING */}
        {loading && (
          <p className="text-muted-foreground text-lg">{t('common.loading')}</p>
        )}

        {/* ERROR */}
        {error && (
          <p className="text-red-500">{t('common.error')}</p>
        )}

        {/* CAROUSEL */}
        {!loading && festivals.length > 0 && (
          <div ref={carouselRef}>
            <Carousel 
              items={carouselItems} 
              showItemsCount={4.5}
              title={t('sections.culturalFestivals')}
              paragraph={t('sections.upcomingEvents')}
              type="festivals"
            />
          </div>
        )}

      </div>
    </section>
  )
}